import '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle.scss';

import ColorButton                   from '@/TokamapEditor/ux/AreaSidebar/ColorButton';
import Button                        from '@widesk-ui/components/Button';
import MixedSelect                   from '@/TokamapEditor/ux/AreaSidebar/MixedSelect';
import View                          from '@widesk-ui/components/View';
import Field                         from '@/TokamapEditor/ux/AreaSidebar/Field';
import NormalHoverToggle             from '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle';
import useHoverPreview               from '@/TokamapEditor/helpers/useHoverPreview';
import { type NormalHoverScope }     from '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle';
import currentEditorStore            from '@/TokamapEditor/stores/currentEditorStore';
import { observer }                  from 'mobx-react-lite';
import { DrawTriggerOptions }        from '@tokamap/core';
import { type Shape }                from '@tokamap/core';
import { type ShapeStyle }           from '@tokamap/core';
import SliderInputNumberFeatureStyle from '@/TokamapEditor/ux/AreaSidebar/SliderInputNumberFeatureStyle';
import SwitchField                   from '@/TokamapEditor/ux/SwitchField';
import { commonValue }               from '@/TokamapEditor/ux/AreaSidebar/multiSelectHelpers';
import React                         from 'react';
import useDesignTokens               from '@/hooks/useDesignTokens';
import { DisconnectOutlined }        from '@ant-design/icons';
import themeStore                    from '@/TokamapEditor/stores/themeStore';
import ThemeSwatch                   from '@/TokamapEditor/ux/ThemeSwatch';
import ThreeDOptions                 from '@/TokamapEditor/ux/AreaSidebar/ThreeDOptions';
import CollapsiblePanel              from '@/TokamapEditor/ux/AreaSidebar/CollapsiblePanel';
import HelpTip                       from '@/TokamapEditor/ux/AreaSidebar/HelpTip';
import asSingleHistoryEntry          from '@/TokamapEditor/helpers/asSingleHistoryEntry';
import Segmented                     from '@widesk-ui/Segmented';
import Alert                         from '@widesk-ui/components/Alert';
import { zoomAppearanceOptions }     from '@/TokamapEditor/helpers/zoomAppearance';
import { mercatorToLngLat }          from '@tokamap/core';
import { rectAroundPoints }          from '@tokamap/core';
import { shapesExtentPoints }        from '@tokamap/core';

const STATE_NOTES: Record<NormalHoverScope, string> = {
	normal: `Les couleurs et l'épaisseur ci-dessous s'appliquent à l'état normal.`,
	hover: `Les couleurs et l'épaisseur ci-dessous s'appliquent quand le visiteur survole la forme.`,
};

// Valeurs de `Shape.pathPriority` — voir CLAUDE.md "Itinéraire / pathfinding" (`@tokamap/core`) :
// numérique côté lib (`pathPriorityWeight` décroît en continu avec cette valeur), relibellé ici en
// 3 paliers "attractivité" (Faible/Moyenne/Forte) pour rester compréhensible sans exposer l'échelle
// numérique brute à l'utilisateur.
const PATH_PRIORITY_OPTIONS: { label: string; value: NonNullable<Shape['pathPriority']> | ''; }[] = [
	{ label: 'Aucune', value: '' },
	{ label: 'Faible', value: 1 },
	{ label: 'Moyenne', value: 3 },
	{ label: 'Forte', value: 6 },
];

const AreaThemeSection = observer(() => {
	const [scope, setScope] = React.useState<NormalHoverScope>('normal');

	useHoverPreview(scope === 'hover');

	// Dès qu'UNE forme de la sélection est liée à un thème, toute son apparence vient du thème : le
	// rendu remplace son `style`/`hoverStyle` (voir `applyShapeTheme` dans `@tokamap/core`), y compris
	// pour les champs que le thème laisse vides. Les réglages par forme ci-dessous seraient donc sans
	// effet visible — on les retire plutôt que de les laisser mentir.
	const themed = currentEditorStore.sidebarShapes.some(shape => !!shape.themeId);

	return (
		<View gap="lg">
			{/* En premier : c'est lui qui décide si les réglages suivants s'appliquent. `|| themed` : si
			    le projet n'a plus de thème mais qu'une forme en référence encore un (thème supprimé), le
			    champ reste affiché — sinon plus aucun moyen de la délier. */}
			{(!!themeStore.themes.length || themed) && <ThemeField />}

			{/* Le `!themed` englobe l'enveloppe elle-même : la garder en la vidant laisserait un enfant
			    flex vide, dont le `gap` du parent creuserait quand même un espace. */}
			{!themed && (
				<View gap="md">
					<NormalHoverToggle value={scope} onChange={setScope} help={STATE_NOTES[scope]} />

					<Fields key={scope} hover={scope === 'hover'} />
				</View>
			)}

			{/* Une forme thémée n'a plus de réglages Normal / Au survol : ses options 3D restent seules. */}
			{themed && <ThreeDOptions />}

			<WalkableField />
		</View>
	);
});

export default AreaThemeSection;

/**
 * Lie la ou les formes sélectionnées à un thème (`Shape.themeId`). Placé juste au-dessus des options
 * avancées : c'est un réglage de rattachement, pas un réglage d'apparence — et dès qu'il est
 * renseigné, les champs d'apparence au-dessus disparaissent (voir `themed` dans
 * `AreaThemeSection`), le thème définissant à lui seul le rendu des formes liées.
 */
const ThemeField = observer(() => {
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;

	if (!shapes.length) return null;

	// Même piège que `pathPriority` plus bas : `commonValue` ne distingue pas "aucune forme n'a de
	// thème" de "mélange", les deux renvoyant `undefined` — d'où le `Set` dédié.
	const mixed = new Set(shapes.map(s => s.themeId)).size > 1;
	// `undefined` et non `''` : seul `undefined` ne correspond à aucune option et laisse le placeholder
	// s'afficher (voir le commentaire de `MixedSelect`).
	const themeId = shapes[0]!.themeId;
	const themed = shapes.some(shape => !!shape.themeId);

	const setThemeId = (value: string | undefined) => editor.setShapeThemeId(shapes.map(s => s.id), value);

	return (
		<Field
			label={(
				<>
					Thème
					<HelpTip>
						Sans thème, l'apparence de la forme se règle ici. Avec un thème, elle vient entièrement du
						thème ; ses réglages personnalisés sont conservés et reviennent si vous le retirez.
					</HelpTip>
				</>
			)}
		>
			<View gap="xs">
				<MixedSelect
					value={themeId}
					mixed={mixed}
					placeholder="Aucun thème"
					onChange={value => setThemeId(value ? String(value) : undefined)}
					// Plus d'option "Aucun" : délier passe par le bouton ci-dessous, plus explicite qu'une
					// entrée de liste à retrouver parmi les thèmes.
					options={themeStore.themes.map(theme => ({
						label: (
							<View row gap="xs" centerV>
								<ThemeSwatch theme={theme} />

								{theme.name}
							</View>
						),
						value: theme.id,
					}))}
				/>

				{themed && (
					// Lien plutôt que bouton bordé, mais AVEC l'icône : seule, la mention textuelle passait
					// inaperçue ; le maillon brisé lui donne un point d'accroche visuel sans le poids d'un
					// bouton. `padding: 0` pour que le texte finisse au ras du bord droit du select.
					<Button
						type="link"
						size="small"
						icon={<DisconnectOutlined />}
						style={{ alignSelf: 'flex-end', padding: 0, height: 'auto' }}
						onClick={() => setThemeId(undefined)}
					>
						Retirer le thème
					</Button>
				)}
			</View>
		</Field>
	);
});

const WalkableField = observer(() => {
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;

	if (!shapes.length) return null;

	// Le Switch affiche l'inverse de `walkable` (label "Bloque le passage" — voir CLAUDE.md
	// "Itinéraire / pathfinding" : absent/false = obstacle).
	const blocksPassage = commonValue(shapes, s => !(s.walkable ?? false));
	const mixed = blocksPassage === undefined;

	const setBlocksPassage = (checked: boolean) => {
		editor.setShapeWalkable(shapes.map(s => s.id), !checked);
	};

	const locked = commonValue(shapes, s => !!s.locked);
	const lockedMixed = locked === undefined;

	const setLocked = (checked: boolean) => {
		editor.setShapeLocked(shapes.map(s => s.id), checked);
	};

	// `commonValue` ne distingue pas "toutes les formes ont `pathPriority` absent" de "mélange" (les
	// deux renvoient `undefined`) — même piège que `isMixed` plus bas pour `hoverStyle` : vérification
	// dédiée via un `Set` des valeurs réellement présentes.
	const pathPriorityMixed = new Set(shapes.map(s => s.pathPriority)).size > 1;
	const pathPriority = shapes[0]!.pathPriority ?? '';

	const setPathPriority = (value: unknown) => {
		editor.setShapePathPriority(shapes.map(s => s.id), (value === '' || value === null || value === undefined ? undefined : value) as Shape['pathPriority']);
	};

	return (
		<CollapsiblePanel title="Options avancées">
			<SwitchField
				label={<>Bloque le passage<HelpTip>La forme agit comme un obstacle pour les itinéraires piétons.</HelpTip></>}
				value={!mixed && !!blocksPassage}
				mixed={mixed}
				onChange={setBlocksPassage}
			/>

			<SwitchField
				label={<>Verrouillée<HelpTip>La forme ne peut plus être déplacée, copiée ou supprimée.</HelpTip></>}
				value={!lockedMixed && !!locked}
				mixed={lockedMixed}
				onChange={setLocked}
			/>

			<MinZoomOffsetField />

			<Field
				label={(
					<>
						Attractivité
						<HelpTip>
							{mixed ? (
							'Incite les itinéraires piétons à passer par cette forme, ou à la longer si elle bloque le passage (mélangé sur la sélection actuelle) — plus l\'attractivité est forte, plus le détour accepté pour ça est grand.'
						) : blocksPassage ? (
							'Cette forme reste infranchissable, mais les itinéraires piétons la longeront plutôt que de s\'en éloigner (plus l\'attractivité est forte, plus le détour accepté pour ça est grand).'
						) : (
								'Les itinéraires piétons seront incités à passer par cette forme (plus l\'attractivité est forte, plus le détour accepté pour ça est grand).'
							)}
						</HelpTip>
					</>
				)}
			>
				<MixedSelect
					value={pathPriority}
					mixed={pathPriorityMixed}
					onChange={setPathPriority}
					options={PATH_PRIORITY_OPTIONS}
				/>
			</Field>
		</CollapsiblePanel>
	);
});

const MinZoomOffsetField = observer(() => {
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;
	const referenceZoom = editor.mapAdapter.getStartCamera()?.zoom ?? currentEditorStore.documentOrigin?.zoom;

	const themeOffset = shapes
		.map(shape => (shape.themeId ? themeStore.getTheme(shape.themeId)?.minZoomOffset : undefined))
		.find(Boolean);

	const mixed = new Set(shapes.map(s => s.minZoomOffset)).size > 1;
	const offset = shapes[0]!.minZoomOffset;

	const showAppearanceZoom = (minZoomOffset: number | undefined) => {
		if (referenceZoom === undefined) return;

		const rect = rectAroundPoints(shapesExtentPoints(shapes), 0);

		editor.mapAdapter.animateToView({
			zoom: referenceZoom + (minZoomOffset ?? 0),
			...(rect ? { center: mercatorToLngLat(rect.center) } : {}),
		});
	};

	return (
		<Field
			label={(
				<>
					Apparition au zoom
					<HelpTip>
						La forme n'apparaît qu'une fois la carte zoomée d'autant depuis sa vue de départ, dans l'aperçu
						et la carte publiée. Dans l'éditeur, elle reste affichée mais estompée tant que ce zoom n'est
						pas atteint.
					</HelpTip>
				</>
			)}
		>
			<View gap="xs">
				{themeOffset ? (
					<Alert message="Le zoom d'apparition vient du thème lié. Retirez le thème pour le régler sur cette forme." />
				) : (
					<MixedSelect
						value={offset ?? ''}
						mixed={mixed}
						options={zoomAppearanceOptions(offset)}
						onChange={value => {
							const minZoomOffset = typeof value === 'number' ? value : undefined;

							editor.setShapeMinZoomOffset(shapes.map(s => s.id), minZoomOffset);
							showAppearanceZoom(minZoomOffset);
						}}
					/>
				)}
			</View>
		</Field>
	);
});

const Fields = observer(({ hover }: { hover?: boolean; }) => {
	const editor = currentEditorStore.get;
	const tokens = useDesignTokens();

	const shapes = currentEditorStore.sidebarShapes;
	const isLine = shapes.some(s => s.shapeKind === 'line');

	const apply = async (
		name: string,
		v: string | number | boolean | undefined,
		options?: DrawTriggerOptions,
		remove = false,
	) => {
		const method = hover ? 'setShapeHoverStyle' : 'setShapeStyle';
		const value: ShapeStyle = remove ? {
			bgColor: undefined,
			borderColor: undefined,
			borderWidth: undefined,
			borderWidthUnit: undefined,
			borderDashed: undefined,
		} : { [name]: v };

		const ids = shapes.map(s => s.id);

		if (!remove) return editor[method](ids, value, options);

		// Réinitialiser le survol rend aussi ses hauteurs à celles du repos, en un seul Annuler.
		await asSingleHistoryEntry(() => {
			editor[method](ids, value, options);
			editor.setShapeHoverHeight(ids, undefined, options);
			editor.setShapeHoverElevation(ids, undefined, options);
		});
	};

	const styleKey = hover ? 'hoverStyle' : 'style';

	const style = {
		borderColor: commonValue(shapes, s => s[styleKey]?.borderColor),
		bgColor: commonValue(shapes, s => s[styleKey]?.bgColor),
		borderWidth: commonValue(shapes, s => s[styleKey]?.borderWidth),
		borderWidthUnit: commonValue(shapes, s => s[styleKey]?.borderWidthUnit ?? 'px'),
		borderDashed: commonValue(shapes, s => s[styleKey]?.borderDashed),
	};

	// `undefined` (mixte) est distingué de "non défini pour toutes les formes" (`false`) : une forme
	// qui n'a jamais eu de style au survol a `hoverStyle` absent partout, donc `commonValue` renvoie
	// `undefined` pour CHAQUE propriété dans ce cas précis — indiscernable d'un vrai mélange sans
	// cette vérification dédiée.
	const isMixed = (key: 'borderColor' | 'bgColor' | 'borderWidth' | 'borderDashed') => {
		if (shapes.length < 2) return false;
		const values = shapes.map(s => s[styleKey]?.[key]);
		return new Set(values).size > 1;
	};

	const isCreated = !!hover && (
		[style.borderColor, style.bgColor, style.borderWidth, style.borderDashed].some(Boolean)
		|| shapes.some(s => s.hoverHeight !== undefined || s.hoverElevation !== undefined)
	);
	const inMeters = style.borderWidthUnit === 'm';

	return (
		<View gap="lg" className={hover ? 'normal-hover-fields--hover' : 'normal-hover-fields--normal'}>
			{hover && isCreated && (
				<View row spread centerV>
					<View color={tokens.text.muted} size={12}>Style au survol personnalisé</View>

					<Button size="small" onClick={() => apply('borderColor', '', {}, true)}>
						Réinitialiser
					</Button>
				</View>
			)}

			<Field label="Couleurs">
				<View row gap={6} wrap>
					{!isLine && (
						<ColorButton
							label="Fond"
							value={style?.bgColor}
							mixed={isMixed('bgColor')}
							onChange={color => apply('bgColor', color)}
						/>
					)}

					<ColorButton
						label="Bordure"
						value={style?.borderColor}
						mixed={isMixed('borderColor')}
						onChange={color => apply('borderColor', color)}
					/>
				</View>
			</Field>

			<Field label="Épaisseur de bordure">
				<SliderInputNumberFeatureStyle
					property="borderWidth"
					initialValue={style?.borderWidth || 1}
					toModelValue={v => v}
					fromModelValue={v => v}
					apply={apply}
					mixed={isMixed('borderWidth')}
					inputProps={inMeters ? { min: 0.1, max: 10, step: 0.1 } : { min: 1, max: 20 }}
					unitSelect={{
						value: inMeters ? 'm' : 'px',
						options: [
							{ value: 'px', label: 'px' },
							{ value: 'm', label: 'm' },
						],
						title: 'px : même épaisseur à l\'écran, quel que soit le zoom. m : épaisseur au sol, qui suit le zoom.',
						// Nouvelle unité, épaisseur repartie de 1 : garder le chiffre n'aurait pas de sens.
						onChange: unit => editor[hover ? 'setShapeHoverStyle' : 'setShapeStyle'](
							shapes.map(s => s.id),
							{ borderWidthUnit: unit === 'm' ? 'm' : undefined, borderWidth: 1 },
						),
					}}
				/>
			</Field>

			<View row spread centerV>
				<View size={13} style={{ fontWeight: 600 }}>Trait de la bordure</View>

				<Segmented
					size="small"
					value={isMixed('borderDashed') ? undefined : (style?.borderDashed ? 'dashed' : 'solid')}
					onChange={value => apply('borderDashed', value === 'dashed' || undefined)}
					options={[
						{ value: 'solid', label: 'Plein' },
						{ value: 'dashed', label: 'Pointillés' },
					]}
				/>
			</View>

			<ThreeDOptions hover={hover} />
		</View>
	);
});
